import React, { useCallback } from "react";
import { StepsGroup } from "./types";
import styles from "./FareInfographic.module.scss";
import clsx from "clsx";
import { Icon } from "../Icon/Icon";
import { isPresent } from "../utils/checks";
import { FareInfographicStep } from "./FareInfographicStep";

export type FareInfographicVariant =
  | "default"
  | "full-horizontal"
  | "always-vertical";

export type FareInfographicProps = {
  onInclusionClick?: (stepsGroupId: string) => void;
  steps: StepsGroup[];
  expandable: boolean;
  isExpanded?: boolean;
  variant?: FareInfographicVariant;
};

export const FareInfographic: React.FC<FareInfographicProps> = ({
  onInclusionClick,
  expandable,
  isExpanded = false,
  variant = "default",
  steps = []
}) => {
  return (
    <div
      className={clsx(styles.container, {
        [styles.variantDefault]: variant == "default",
        [styles.variantFullHorizontal]: variant == "full-horizontal",
        [styles.variantAlwaysVertical]: variant == "always-vertical",
        [styles.expandable]: expandable,
        [styles.static]: !expandable,
        [styles.isExpanded]: isExpanded,
        [styles.isCollapsed]: !isExpanded
      })}
    >
      <ul className={styles.groups}>
        {steps.map((g, index) => (
          <React.Fragment key={g.id}>
            <FareInfographicGroup
              index={index}
              isExpanded={variant == "full-horizontal" || isExpanded}
              onInclusionClick={onInclusionClick}
              {...g}
            />
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

type FareInfographicGroupProps = StepsGroup & {
  index: number;
  isExpanded: boolean;
  onInclusionClick?: (stepsGroupId: string) => void;
};
const FareInfographicGroup: React.FC<FareInfographicGroupProps> = ({
  id,
  icon,
  title,
  steps,
  isExpanded,
  onInclusionClick,
  index
}) => {
  const isDisabled = steps.every((s) => s.type == "disabled");
  const renderSteps = isExpanded;

  const withCallback = isPresent(onInclusionClick);
  const onClick = useCallback(
    () => onInclusionClick?.(id),
    [id, onInclusionClick]
  );

  return (
    <React.Fragment>
      {index != 0 && (
        <li
          className={clsx(styles.separator, {
            [styles.separatorDisabled]: isDisabled
          })}
        >
          <hr />
        </li>
      )}

      <li
        key={id}
        className={clsx(styles.group, {
          [styles.disabled]: isDisabled,
          [styles.isShorex]: id == "shorex",
          [styles.isPreCruise]: id == "pre-cruise",
          [styles.isPostCruise]: id == "post-cruise"
        })}
      >
        <ButtonOrDiv
          trackingName={title}
          onClick={withCallback ? onClick : undefined}
          className={styles.groupHeaderIcon}
          disabled={isDisabled}
        >
          <Icon name={icon} size={undefined} padding={false} />
        </ButtonOrDiv>
        <ButtonOrDiv
          trackingName={title}
          onClick={withCallback ? onClick : undefined}
          className={clsx(styles.groupHeader, {
            [styles.groupHeaderUnderline]: withCallback && !isDisabled
          })}
          disabled={isDisabled}
        >
          {title}
        </ButtonOrDiv>

        {renderSteps && (
          <ul className={styles.steps}>
            {steps.map((s) => (
              <FareInfographicStep key={s.id} {...s} />
            ))}
          </ul>
        )}
      </li>
    </React.Fragment>
  );
};

type BaseButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;
const ButtonOrDiv: React.FC<
  React.PropsWithChildren<
    Omit<BaseButtonProps, "onClick"> & {
      onClick?: BaseButtonProps["onClick"];
      disabled?: boolean;
      trackingName: string;
    }
  >
> = ({
  onClick,
  disabled = false,
  children,
  trackingName,
  className,
  ...otherProps
}) => {
  if (isPresent(onClick) && !disabled)
    return (
      <button
        onClick={onClick}
        className={className}
        data-content-piece={
          !disabled ? `infographic_inclusion_${trackingName}` : undefined
        }
        {...otherProps}
      >
        {children}
      </button>
    );
  else {
    return <div className={className}>{children}</div>;
  }
};
